import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import API_BASE_URL from '../config/apiBase';
import './AirplaneDetailModal.css';
import './PoiDetailOverlay.css';

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  return String(value).trim() !== '';
};

const formatCoordinate = (value, positiveLabel, negativeLabel) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return `${Math.abs(numericValue).toFixed(3)}° ${numericValue >= 0 ? positiveLabel : negativeLabel}`;
};

const buildStatusCopy = (fetchState) => {
  if (fetchState === 'loading') return '加载中';
  if (fetchState === 'ready') return '已同步';
  if (fetchState === 'error') return '加载失败';
  if (fetchState === 'missing') return '仅基础索引';
  return '基础信息';
};

const buildFallbackLead = (fetchState) => {
  if (fetchState === 'loading') {
    return '正在从后台加载机场资料，稍后会自动填充这座机场的运营概况、航站楼信息与亮点内容。';
  }

  if (fetchState === 'error') {
    return '机场资料加载失败，当前先展示地球模块里的基础 POI 信息，稍后可以重试或检查后台接口。';
  }

  return '后台暂未录入这座机场的 overlay 内容，当前先展示基础地理信息。录入机场资料后，这里会自动切换为真实内容。';
};

const formatPassengerMillions = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }

  const numericValue = Number(value);
  return numericValue >= 100 ? numericValue.toFixed(0) : numericValue.toFixed(1);
};

const getPoiLookupCode = (poi) => poi?.lookupCode || poi?.iata || poi?.code || poi?.icao || null;

const getPoiPrimaryCode = (poi) => poi?.iata || poi?.code || poi?.icao || 'AIRPORT';

const getPoiCodeEntries = (poi) => {
  const entries = [];
  const iata =
    poi?.iata || (!poi?.icao && poi?.code && poi.code.length === 3 ? poi.code : null);
  const icao =
    poi?.icao || (!poi?.iata && poi?.code && poi.code.length === 4 ? poi.code : null);

  if (iata) {
    entries.push({ label: 'IATA', value: iata });
  }

  if (icao && icao !== iata) {
    entries.push({ label: 'ICAO', value: icao });
  }

  if (!entries.length && poi?.code) {
    entries.push({ label: '代码', value: poi.code });
  }

  return entries;
};

const detailMatchesLookup = (airportDetail, lookupCode) => {
  if (!airportDetail || !lookupCode) return false;
  const normalizedLookup = String(lookupCode).trim().toUpperCase();
  return [
    airportDetail.code,
    airportDetail.lookup_code,
    airportDetail.display_code,
    airportDetail.iata_code,
    airportDetail.icao_code,
  ]
    .filter(Boolean)
    .some((code) => String(code).trim().toUpperCase() === normalizedLookup);
};

const valueOrFallback = (...values) => values.find((value) => hasValue(value));

const buildRows = (rows) =>
  rows.filter((row) => hasValue(row.value)).map((row) => ({
    ...row,
    value: String(row.value),
  }));

export default function PoiDetailOverlay({ poi, onClose }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDeferredContentReady, setIsDeferredContentReady] = useState(false);
  const [airportDetail, setAirportDetail] = useState(null);
  const [airportFetchState, setAirportFetchState] = useState('idle');
  const scrollContainerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastPoiRef = useRef(null);
  const scrollFrameRef = useRef(0);
  const open = Boolean(poi);
  const lookupCode = getPoiLookupCode(poi);

  useEffect(() => {
    if (poi) {
      lastPoiRef.current = poi;
    }
  }, [poi]);

  useEffect(() => {
    if (!lookupCode) {
      return undefined;
    }

    const controller = new AbortController();

    const loadAirportDetail = async () => {
      setAirportFetchState('loading');

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/airports/code/${encodeURIComponent(lookupCode)}`,
          { signal: controller.signal }
        );

        if (response.status === 404) {
          setAirportDetail(null);
          setAirportFetchState('missing');
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch airport detail: ${response.status}`);
        }

        const data = await response.json();
        setAirportDetail(data);
        setAirportFetchState('ready');
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        console.error('机场 overlay 数据加载失败:', error);
        setAirportDetail(null);
        setAirportFetchState('error');
      }
    };

    loadAirportDetail();

    return () => {
      controller.abort();
    };
  }, [lookupCode]);

  useEffect(() => {
    if (!lookupCode) {
      return undefined;
    }

    setIsDeferredContentReady(false);

    let firstFrameId = 0;
    let secondFrameId = 0;

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setIsDeferredContentReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrameId);
      window.cancelAnimationFrame(secondFrameId);
    };
  }, [lookupCode]);

  useEffect(() => {
    if (poi) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
      setIsScrolled(false);
      closeButtonRef.current?.focus();
    }
  }, [poi]);

  useEffect(
    () => () => {
      if (scrollFrameRef.current) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    },
    []
  );

  const currentPoi = poi ?? lastPoiRef.current;
  const deferredContentReady = isDeferredContentReady;

  if (!currentPoi && !open) {
    return null;
  }

  const currentLookupCode = getPoiLookupCode(currentPoi);
  const primaryCode = getPoiPrimaryCode(currentPoi);
  const poiCodeEntries = getPoiCodeEntries(currentPoi);
  const airportData = detailMatchesLookup(airportDetail, currentLookupCode) ? airportDetail : null;
  const statusCopy = buildStatusCopy(airportFetchState);
  const leadText = airportData?.description || buildFallbackLead(airportFetchState);
  const heroTitle = airportData?.name || currentPoi.name || primaryCode;
  const heroSubtitle = airportData?.subtitle || leadText;
  const airportCity = valueOrFallback(airportData?.city, currentPoi.city);
  const airportCountry = valueOrFallback(airportData?.country, currentPoi.country);
  const airportLatitude = valueOrFallback(airportData?.latitude, currentPoi.lat);
  const airportLongitude = valueOrFallback(airportData?.longitude, currentPoi.lon);
  const formattedLatitude = formatCoordinate(airportLatitude, 'N', 'S');
  const formattedLongitude = formatCoordinate(airportLongitude, 'E', 'W');
  const coordinateText =
    formattedLatitude && formattedLongitude ? `${formattedLatitude} / ${formattedLongitude}` : null;
  const locationText = [airportCity, airportCountry].filter(Boolean).join(' / ');
  const heroTags = Array.from(new Set([
    locationText || null,
    airportData?.airport_type || null,
    airportData?.timezone || null,
  ].filter(Boolean)));

  const handleScroll = (event) => {
    const nextIsScrolled = event.currentTarget.scrollTop > 10;
    if (scrollFrameRef.current) {
      window.cancelAnimationFrame(scrollFrameRef.current);
    }
    scrollFrameRef.current = window.requestAnimationFrame(() => {
      setIsScrolled((previousIsScrolled) =>
        previousIsScrolled === nextIsScrolled ? previousIsScrolled : nextIsScrolled
      );
      scrollFrameRef.current = 0;
    });
  };

  const identityDetails = buildRows([
    { label: 'POI 类型', value: '机场' },
    ...poiCodeEntries,
    { label: '城市', value: airportCity },
    { label: '国家 / 地区', value: airportCountry },
  ]);

  const passengerMetric = formatPassengerMillions(airportData?.annual_passengers_millions);
  const metricCards = [
    airportData?.terminals !== null && airportData?.terminals !== undefined
      ? {
          label: '航站楼',
          value: airportData.terminals,
          unit: '座',
        }
      : null,
    airportData?.runways !== null && airportData?.runways !== undefined
      ? {
          label: '跑道',
          value: airportData.runways,
          unit: '条',
        }
      : null,
    passengerMetric
      ? {
          label: '年旅客吞吐',
          value: passengerMetric,
          unit: '百万人次',
        }
      : airportData?.destinations !== null && airportData?.destinations !== undefined
        ? {
            label: '通航目的地',
            value: airportData.destinations,
            unit: '个',
          }
        : null,
  ].filter(Boolean);

  if (!metricCards.length) {
    metricCards.push({
      label: '数据状态',
      value: statusCopy,
      unit: null,
    });
  }

  const highlightCards = [
    {
      kicker: '机场概览',
      title: `${primaryCode} 机场画像`,
      body: airportData?.description || leadText,
    },
    {
      kicker: '亮点信息',
      title: airportData?.airport_type || '基础索引',
      body:
        airportData?.interesting_fact ||
        airportData?.operations_note ||
        '当前仅展示已核实的机场基础索引和坐标信息。',
    },
  ];

  const contentModules = [
    {
      label: '航站楼与跑道',
      value: airportData?.terminal_and_runway_info,
    },
    {
      label: '航线与联盟',
      value: airportData?.traffic_and_alliance_info,
    },
    {
      label: '运行特点',
      value: airportData?.operations_note,
    },
    {
      label: '地面交通',
      value: airportData?.ground_transport,
    },
  ].filter((item) => hasValue(item.value));

  if (!contentModules.length) {
    contentModules.push({
      label: '基础索引',
      value: [heroTitle, locationText, coordinateText]
        .filter(Boolean)
        .join('；') || '当前仅有机场基础识别信息。',
    });
  }

  const dossierRows = buildRows([
    { label: '同步状态', value: statusCopy, isKey: true },
    { label: '机场名称', value: heroTitle },
    { label: '城市 / 国家', value: locationText },
    { label: '时区', value: airportData?.timezone },
    {
      label: '海拔',
      value:
        airportData?.elevation_m !== null && airportData?.elevation_m !== undefined
          ? `${airportData.elevation_m} m`
          : null,
    },
    {
      label: '通航目的地',
      value:
        airportData?.destinations !== null && airportData?.destinations !== undefined
          ? `${airportData.destinations} 个`
          : null,
    },
    { label: '枢纽航司', value: airportData?.hub_airlines },
    { label: '坐标', value: coordinateText },
  ]);

  const heroStyle = airportData?.hero_image_url
    ? {
        backgroundImage: `linear-gradient(135deg, rgba(7, 14, 24, 0.74), rgba(13, 24, 40, 0.78)), url(${airportData.hero_image_url})`,
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        disableDefaultAnimation
        overlayClassName="detail-dialog-overlay detail-dialog-overlay--poi"
        className="detail-dialog detail-dialog--poi w-[min(95vw,1100px)] max-w-none overflow-hidden p-0 focus:outline-none focus-visible:outline-none"
      >
        <DialogTitle className="sr-only">{`${heroTitle} 机场详情`}</DialogTitle>
        <div className="flex h-full flex-col">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="detail-scroll poi-detail-scroll flex-1 overflow-y-auto pb-10"
          >
            <div className="detail-hero-wrap">
              <div
                style={heroStyle}
                className="detail-hero-media poi-detail-hero h-[300px] w-full overflow-hidden border-b border-border/60 shadow-lg sm:h-[380px] lg:h-[440px]"
              >
                <div className="poi-detail-hero-grid">
                  <div className="poi-detail-code-stack">
                    <div className="poi-detail-code-orb">
                      <span>{primaryCode}</span>
                    </div>
                    <div className="poi-detail-coordinates">
                      {formattedLatitude ? <span>{formattedLatitude}</span> : null}
                      {formattedLongitude ? <span>{formattedLongitude}</span> : null}
                    </div>
                  </div>

                  <div className="poi-detail-hero-copy">
                    <p className="detail-kicker">Airport point of interest</p>
                    <h2 className="poi-detail-hero-title">{heroTitle}</h2>
                    <p className="poi-detail-hero-subtitle">{heroSubtitle}</p>

                    <div className="poi-detail-hero-tags" aria-label="机场摘要">
                      {heroTags.map((tag) => (
                        <span className="poi-detail-hero-tag" key={tag}>
                          {tag}
                        </span>
                      ))}
                      <span
                        className={cn(
                          'poi-detail-hero-tag poi-detail-hero-tag--status',
                          airportFetchState === 'ready' && 'poi-detail-hero-tag--ready',
                          airportFetchState === 'error' && 'poi-detail-hero-tag--error',
                          airportFetchState !== 'ready' &&
                            airportFetchState !== 'error' &&
                            'poi-detail-hero-tag--pending'
                        )}
                      >
                        {statusCopy}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mx-auto w-full max-w-6xl px-6 pt-10 sm:px-8">
              <div className="detail-content-shell poi-detail-content-shell">
                <section className={cn('detail-intro-grid', isScrolled && 'detail-intro-grid--scrolled')}>
                  <div className="detail-intro-copy">
                    <p className="detail-kicker">Airport overlay</p>
                    <h2 className="detail-title">{heroTitle}</h2>
                    <div className="detail-subtitle-list" aria-label="机场摘要">
                      {Array.from(
                        new Set([
                          primaryCode,
                          airportData?.icao_code && airportData.icao_code !== primaryCode
                            ? airportData.icao_code
                            : null,
                          currentPoi.icao && currentPoi.icao !== primaryCode ? currentPoi.icao : null,
                          ...heroTags,
                        ].filter(Boolean))
                      ).map((item) => (
                        <span key={item}>{item}</span>
                      ))}
                    </div>
                    <p className="detail-lead">{leadText}</p>
                  </div>

                  <div className="detail-identity-card" aria-label="机场身份信息">
                    {identityDetails.map((item) => (
                      <div className="detail-identity-item" key={item.label}>
                        <p className="detail-identity-label">{item.label}</p>
                        <p className="detail-identity-value">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="detail-metric-grid" aria-label="机场快速指标">
                  {metricCards.map((item) => (
                    <article className="detail-metric-card" key={item.label}>
                      <p className="detail-metric-label">{item.label}</p>
                      <p className="detail-metric-value">
                        {item.value}
                        {item.unit ? <span className="detail-metric-unit">{item.unit}</span> : null}
                      </p>
                    </article>
                  ))}
                </section>

                {deferredContentReady ? (
                  <div className="detail-body-grid poi-detail-deferred poi-detail-deferred--ready">
                    <div className="detail-main-stack">
                      <section className="detail-highlight-grid">
                        {highlightCards.map((item) => (
                          <article className="detail-highlight-card" key={item.title}>
                            <p className="detail-section-kicker">{item.kicker}</p>
                            <h3 className="detail-highlight-title">{item.title}</h3>
                            <p className="detail-highlight-text">{item.body}</p>
                          </article>
                        ))}
                      </section>

                      <section className="detail-panel">
                        <p className="detail-section-kicker">机场模块</p>
                        <h3 className="detail-section-title">运行与出行信息</h3>
                        <div className="detail-section-body">
                          <div className="poi-detail-module-list">
                            {contentModules.map((item) => (
                              <div className="poi-detail-module-card" key={item.label}>
                                <span className="poi-detail-module-dot" aria-hidden="true" />
                                <div className="poi-detail-module-copy">
                                  <p className="detail-section-kicker">{item.label}</p>
                                  <p className="detail-note poi-detail-note">{item.value}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </section>
                    </div>

                    <aside className="detail-rail">
                      <section className="detail-panel detail-dossier">
                        <div className="detail-dossier-header">
                          <p className="detail-section-kicker">机场速览</p>
                          <h3 className="detail-section-title">{heroTitle}</h3>
                          <p className="detail-dossier-text">
                            {airportData?.subtitle ||
                              '这里汇总这座机场的关键信息，方便在地球模块里快速查看。'}
                          </p>
                        </div>

                        <div className="detail-dossier-group">
                          <h4 className="detail-dossier-group-title">关键字段</h4>
                          <div className="detail-dossier-grid">
                            {dossierRows.map((item) => (
                              <div
                                className={cn(
                                  'detail-spec-row',
                                  item.isKey && 'detail-spec-row--key'
                                )}
                                key={item.label}
                              >
                                <span className="detail-spec-label">{item.label}</span>
                                <span className="detail-spec-value">{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </section>
                    </aside>
                  </div>
                ) : (
                  <div className="poi-detail-deferred-placeholder" aria-hidden="true">
                    <span className="poi-detail-placeholder-line poi-detail-placeholder-line--long" />
                    <span className="poi-detail-placeholder-line poi-detail-placeholder-line--mid" />
                    <span className="poi-detail-placeholder-line poi-detail-placeholder-line--short" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogClose asChild>
          <Button
            ref={closeButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            className="detail-close-button absolute right-4 top-4 rounded-full shadow-md"
            aria-label="Close"
          >
            <span className="text-lg">x</span>
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
